import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import Swal from "sweetalert2";
// Ensure SweetAlert2 appears above MUI Dialogs

const emptyForm = { at_range: "", fine_amount: "" };
const redButton = {
  backgroundColor: "#d32f2f",
  color: "#fff !important",
  "&:hover": { backgroundColor: "#b71c1c" },
};

const whiteButtonText = {
  color: "#fff !important",
  "& .MuiButton-startIcon": { color: "#fff !important" },
};

export default function AttendanceFineRange() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const userId = user?.user_id;
  const [scope, setScope] = useState("");
  const [acyearId, setAcyearId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, row: null });
  const [form, setForm] = useState(emptyForm);
  // Keep SweetAlert2 above Material UI Dialogs
  const swalOnTop = (options) => {
    const originalDidOpen = options.didOpen;

    return Swal.fire({
      target: document.body,
      ...options,
      didOpen: (popup) => {
        const container = Swal.getContainer();

        if (container) {
          container.style.setProperty("z-index", "20000", "important");
        }

        // Preserve any existing didOpen callback
        originalDidOpen?.(popup);
      },
    });
  };

  const showMessage = (text, severity = "success") =>
    swalOnTop({
      title:
        severity === "error"
          ? "Unable to complete action"
          : severity === "warning"
          ? "Warning"
          : "Success",
      text,
      icon: severity,
      confirmButtonColor: severity === "error" ? "#d32f2f" : "#1976d2",
    });

  const fetchRows = useCallback(async () => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        pageSize: String(pageSize),
        search,
      });
      if (scope) params.set("scope", scope);
      if (scope === "ACADEMIC_YEAR" && acyearId) params.set("acyear_id", acyearId);
      const { data } = await axios.get(
        `${API_BASE_URL}/api/attendance-fine-ranges/${clientId}/${collegeId}?${params}`
      );
      const payload = data?.data || {};
      setRows(
        (payload.items || []).map((row) => ({
          ...row,
          id: row.fine_range_id,
          academic_year_name: row.acyear?.academic_year || "Common",
        }))
      );
      setRowCount(payload.total || 0);
    } catch (error) {
      showMessage(
        error.response?.data?.message || "Unable to load attendance fine ranges.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, clientId, collegeId, scope, acyearId, page, pageSize, search]);

  useEffect(() => {
    if (!clientId || !collegeId) return;
    axios
      .get(`${API_BASE_URL}/api/acyear/by-college/${clientId}/${collegeId}`)
      .then(({ data }) => setAcademicYears(data?.data || []))
      .catch((error) =>
        showMessage(error.response?.data?.message || "Unable to load academic years.", "error")
      );
  }, [API_BASE_URL, clientId, collegeId]);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openDialog = (row = null) => {
    if (row) {
      // Edit: use the selected row's configuration
      setScope(row.config_scope || "");
      setAcyearId(row.acyear_id != null ? String(row.acyear_id) : "");

      setForm({
        at_range: row.at_range ?? "",
        fine_amount: row.fine_amount ?? "",
      });

      setDialog({ open: true, row });
      return;
    }

    // Add: validate the selected filters
    if (!scope || (scope === "ACADEMIC_YEAR" && !acyearId)) {
      showMessage(
        "Select a configuration scope and academic year before adding a fine range.",
        "warning"
      );
      return;
    }

    setForm(emptyForm);
    setDialog({ open: true, row: null });
  };
  const closeDialog = () => {
    setDialog({ open: false, row: null });
    setForm(emptyForm);
  };
  const saveRange = async () => {
    const atRange = Number(form.at_range),
      fineAmount = Number(form.fine_amount);
    if (
      !Number.isFinite(atRange) ||
      atRange < 0 ||
      atRange > 100 ||
      !Number.isFinite(fineAmount) ||
      fineAmount < 0
    ) {
      showMessage(
        "Enter an attendance threshold from 0 to 100 and a non-negative fine amount.",
        "error"
      );
      return;
    }
    const body = {
      config_scope: scope,
      acyear_id: scope === "ACADEMIC_YEAR" ? acyearId : null,
      at_range: atRange,
      fine_amount: fineAmount,
      user_id: userId,
    };
    const confirmed = await swalOnTop({
      title: dialog.row ? "Update fine range?" : "Save fine range?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: dialog.row ? "Update" : "Save",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1976d2",
      cancelButtonColor: "#d32f2f",
    });
    if (!confirmed.isConfirmed) return;
    try {
      let response;
      if (dialog.row)
        response = await axios.put(
          `${API_BASE_URL}/api/attendance-fine-ranges/${clientId}/${collegeId}/${dialog.row.fine_range_id}`,
          body
        );
      else
        response = await axios.post(
          `${API_BASE_URL}/api/attendance-fine-ranges/${clientId}/${collegeId}`,
          body
        );

      const savedRow = response?.data?.data;
      if (savedRow) {
        const mappedRow = {
          ...savedRow,
          id: savedRow.fine_range_id,
          academic_year_name: savedRow.acyear?.academic_year || "Common",
        };
        if (dialog.row) {
          setRows((currentRows) =>
            currentRows.map((row) =>
              String(row.fine_range_id) === String(savedRow.fine_range_id) ? mappedRow : row
            )
          );
        } else {
          setRows((currentRows) => [mappedRow, ...currentRows].slice(0, pageSize));
          setRowCount((currentCount) => currentCount + 1);
        }
      }
      closeDialog();
      showMessage(
        dialog.row ? "Fine range updated successfully." : "Fine range saved successfully."
      );
    } catch (error) {
      showMessage(error.response?.data?.message || "Unable to save fine range.", "error");
    }
  };
  const remove = async (row) => {
    const confirmed = await Swal.fire({
      title: "Delete fine range?",
      text: "Only pending ranges can be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
    });
    if (!confirmed.isConfirmed) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/attendance-fine-ranges/${clientId}/${collegeId}/${row.fine_range_id}`
      );
      showMessage("Fine range deleted successfully.");
      fetchRows();
    } catch (error) {
      showMessage(error.response?.data?.message || "Unable to delete fine range.", "error");
    }
  };

  const submitForApproval = async (row) => {
    const confirmed = await Swal.fire({
      title: "Submit attendance fine configuration?",
      text: "All draft ranges in this configuration will be submitted for approval.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1976d2",
      cancelButtonColor: "#d32f2f",
    });
    if (!confirmed.isConfirmed) return;
    try {
      await axios.patch(
        `${API_BASE_URL}/api/attendance-fine-ranges/${clientId}/${collegeId}/submit`,
        {
          config_scope: row.config_scope,
          acyear_id: row.acyear_id,
          user_id: userId,
        }
      );
      await fetchRows();
      showMessage("Attendance fine configuration submitted for approval.");
    } catch (error) {
      showMessage(error.response?.data?.message || "Unable to submit configuration.", "error");
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 75,
        valueGetter: ({ api, id }) => api.getRowIndexRelativeToVisibleRows(id) + 1,
      },
      { field: "config_scope", headerName: "Configuration Scope", minWidth: 180, flex: 1 },
      { field: "academic_year_name", headerName: "Academic Year", minWidth: 140, flex: 0.8 },
      { field: "at_range", headerName: "Attendance [and above] Range", minWidth: 145, flex: 0.7 },
      { field: "fine_amount", headerName: "Fine Amount", minWidth: 130, flex: 0.7 },
      { field: "workflow_status", headerName: "Status", minWidth: 110, flex: 0.6 },
      {
        field: "actions",
        headerName: "Actions",
        width: 310,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              disabled={row.workflow_status !== "DRAFT"}
              onClick={() => openDialog(row)}
              sx={whiteButtonText}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<DeleteIcon />}
              disabled={row.workflow_status !== "DRAFT"}
              sx={{ ...redButton, ...whiteButtonText }}
              onClick={() => remove(row)}
            >
              Delete
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SendIcon />}
              disabled={row.workflow_status !== "DRAFT"}
              onClick={() => submitForApproval(row)}
              sx={whiteButtonText}
            >
              Submit
            </Button>
          </Stack>
        ),
      },
    ],
    [scope, acyearId]
  );

  return (
    <Box p={{ xs: 1, sm: 2 }} sx={{ minWidth: 0 }}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ md: "center" }}
          >
            <Typography variant="h5" p={2}>
              ATTENDANCE FINE RANGES
            </Typography>
            <TextField
              select
              label="Configuration Scope"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                setAcyearId("");
                setPage(0);
              }}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { md: 220 } }}
            >
              <option value="">All Configurations</option>
              <option value="COMMON">COMMON</option>
              <option value="ACADEMIC_YEAR">ACADEMIC YEAR</option>
            </TextField>
            {scope === "ACADEMIC_YEAR" && (
              <TextField
                select
                label="Academic Year"
                value={acyearId}
                onChange={(e) => {
                  setAcyearId(e.target.value);
                  setPage(0);
                }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { md: 180 } }}
              >
                <option value="">All Academic Years</option>
                {academicYears.map((year) => (
                  <option key={year.acyear_id} value={year.acyear_id}>
                    {year.academic_year}
                  </option>
                ))}
              </TextField>
            )}
            <TextField
              label="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ width: { xs: "100%", sm: 240 }, minWidth: 180 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openDialog()}
              sx={{ whiteSpace: "nowrap", ...whiteButtonText }}
            >
              Add Fine Range
            </Button>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
          <Typography variant="h6" p={2}>
            Example: 75 and above- Rs. 0 , 65 and upto 74 -Rs. 10 per one percent, 55 and upto 64 -
            Rs. 20
          </Typography>
          <Box
            sx={{
              height: 480,
              width: "auto",
              minWidth: 0,
              mx: 2,
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              pagination
              paginationMode="server"
              rowCount={rowCount}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(value) => {
                setPageSize(value);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </Box>
        </Paper>
      </Stack>
      <Dialog open={dialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialog.row ? "Edit Fine Range" : "Add Fine Range"}
          <IconButton
            onClick={closeDialog}
            sx={{ position: "absolute", right: 8, top: 8, color: "#d32f2f" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <TextField
              type="number"
              label="Attendance Range ex: 75 and above->Rs. 0 , 65 and above ->Rs. 10  per one percent"
              value={form.at_range}
              onChange={(e) => setForm({ ...form, at_range: e.target.value })}
              inputProps={{ min: 0, max: 100, step: "0.01" }}
              fullWidth
            />
            <TextField
              type="number"
              label="Fine Amount Per Percentage"
              value={form.fine_amount}
              onChange={(e) => setForm({ ...form, fine_amount: e.target.value })}
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            startIcon={<CloseIcon />}
            onClick={closeDialog}
            sx={redButton}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={saveRange} sx={whiteButtonText}>
            {dialog.row ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
